/**
 * Shipping Calculation Utilities
 * 
 * Handles courier charge calculation based on delivery pincode.
 * Pricing: Ahmedabad ₹80, Gujarat ₹100, Rest of India ₹110
 */

import { isServiceable, GUJARAT_DISTRICTS } from '../data/serviceablePincodes';

// Shipping Constants
export const AHMEDABAD_RANGE = { start: 380001, end: 383345 };
export const AHMEDABAD_SHIPPING = 80;
export const OTHER_GUJARAT_SHIPPING = 100;
export const REST_OF_INDIA_SHIPPING = 110;
export const FREE_SHIPPING_THRESHOLD = 2999; // Free shipping above ₹2999

/**
 * Calculate shipping cost based on pincode and optional subtotal
 * @param {string|number} pincode - 6-digit Indian pincode
 * @param {number} subtotal - Optional cart subtotal for free shipping calculation
 * @returns {Object} { amount: number, location: string, isFree: boolean } or { amount: 0, error: string }
 */
export const calculateShipping = (pincode, subtotal = null) => {
    // Validate pincode first
    const serviceCheck = isServiceable(pincode);

    if (!serviceCheck.valid) {
        return {
            amount: 0,
            error: serviceCheck.message || 'Invalid or non-serviceable pincode',
            isFree: false
        };
    }

    // Check for free shipping eligibility
    if (subtotal !== null && subtotal >= FREE_SHIPPING_THRESHOLD) {
        return {
            amount: 0,
            location: serviceCheck.district || 'Gujarat',
            isFree: true,
            message: 'FREE SHIPPING'
        };
    }

    const pin = parseInt(String(pincode).trim(), 10);

    // Check if Ahmedabad
    if (pin >= AHMEDABAD_RANGE.start && pin <= AHMEDABAD_RANGE.end) {
        return {
            amount: AHMEDABAD_SHIPPING,
            location: 'Ahmedabad',
            isFree: false
        };
    }

    // Check if Rest of Gujarat
    if (serviceCheck.state === 'Gujarat') {
        return {
            amount: OTHER_GUJARAT_SHIPPING,
            location: serviceCheck.district || 'Gujarat',
            isFree: false
        };
    }

    // Rest of India
    return {
        amount: REST_OF_INDIA_SHIPPING,
        location: 'India',
        isFree: false
    };
};

/**
 * Extract pincode from address string
 * Looks for 6-digit number, prioritizing patterns like "- 380001" or "PIN: 380001"
 * @param {string} address - Full address string
 * @returns {string|null} - Extracted 6-digit pincode or null
 */
export const extractPincodeFromAddress = (address) => {
    if (!address || typeof address !== 'string') {
        return null;
    }

    // Pattern 1: Look for "- 6digits" or ", 6digits" (common format)
    const dashPattern = /[-,]\s*(\d{6})\b/;
    const dashMatch = address.match(dashPattern);
    if (dashMatch) {
        return dashMatch[1];
    }

    // Pattern 2: Look for "PIN: 6digits" or "Pincode: 6digits"
    const pinPattern = /\b(?:PIN|Pincode|pincode|pin code)\s*:?\s*(\d{6})\b/i;
    const pinMatch = address.match(pinPattern);
    if (pinMatch) {
        return pinMatch[1];
    }

    // Pattern 3: Any 6-digit number (fallback)
    const generalPattern = /\b(\d{6})\b/;
    const generalMatch = address.match(generalPattern);
    if (generalMatch) {
        return generalMatch[1];
    }

    return null;
};

/**
 * Calculate shipping from address string
 * Convenience function that extracts pincode and calculates shipping
 * @param {string} address - Full address string
 * @param {number} subtotal - Optional cart subtotal for free shipping
 * @returns {Object} { amount: number, location: string, pincode: string|null, isFree: boolean }
 */
export const calculateShippingFromAddress = (address, subtotal = null) => {
    const pincode = extractPincodeFromAddress(address);

    if (!pincode) {
        return {
            amount: 0,
            location: null,
            pincode: null,
            isFree: false,
            error: 'Unable to extract pincode from address'
        };
    }

    const result = calculateShipping(pincode, subtotal);
    return {
        ...result,
        pincode
    };
};

/**
 * Format shipping cost for display
 * @param {number} amount - Shipping amount
 * @returns {string} - Formatted string like "₹80"
 */
export const formatShippingCost = (amount) => {
    return `₹${amount}`;
};

/**
 * Get saved shipping info from localStorage
 * @returns {Object|null} - { pincode, amount, location } or null
 */
export const getSavedShippingInfo = () => {
    try {
        const saved = localStorage.getItem('shippingInfo');
        return saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
};

/**
 * Save shipping info to localStorage
 * @param {string} pincode 
 * @param {number} amount 
 * @param {string} location 
 */
export const saveShippingInfo = (pincode, amount, location) => {
    try {
        localStorage.setItem('shippingInfo', JSON.stringify({
            pincode,
            amount,
            location,
            timestamp: Date.now()
        }));
    } catch {
        // localStorage not available
    }
};

/**
 * Clear saved shipping info
 */
export const clearShippingInfo = () => {
    try {
        localStorage.removeItem('shippingInfo');
    } catch {
        // localStorage not available
    }
};

/**
 * Calculate free shipping progress
 * @param {number} subtotal - Current cart subtotal
 * @returns {Object} { eligible: boolean, remaining: number, percentage: number }
 */
export const getFreeShippingProgress = (subtotal) => {
    const eligible = subtotal >= FREE_SHIPPING_THRESHOLD;
    const remaining = eligible ? 0 : FREE_SHIPPING_THRESHOLD - subtotal;
    const percentage = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

    return {
        eligible,
        remaining,
        percentage,
        threshold: FREE_SHIPPING_THRESHOLD
    };
};
